import React from "react";
import "../gradientAccents.css";

/**
 * Wizard step 1 — name the sequence and decide whether it carries a screening form.
 * The name field mirrors SharedStep1_JobDetails' pill input from the job-role flow (minus the
 * autocomplete dropdown); the screening choice is a single Yes/No toggle.
 *
 * That answer decides the rest of the wizard: No skips straight to the Sequence step, Yes
 * inserts Questions Setup before it. Editable until the first candidate is enrolled; after that
 * `typeLocked` freezes it, because sent emails carry the form link frozen into their body,
 * candidate links are already live, and responses may exist. The server enforces the same rule.
 */
const StepCreate = ({ name, includeScreening, onNameChange, onTypeChange, typeLocked }) => (
    <div className="w-full space-y-6">
        {/* ── Name ──────────────────────────────────────────────────────────────── */}
        {/* text-left is load-bearing: bare `h1` selectors in css/orgadmin.css and
            css/HRPortal.css force text-align:center app-wide. */}
        <h1 className="text-lg font-light text-left dark:text-white text-textPrimary mb-6">
            Let&apos;s begin by adding a few details to create your outreach
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-2">
            <div className="relative">
                <label className="absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1">
                    Outreach Name <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                    type="text"
                    className="w-full px-6 py-3 mt-2 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200"
                    placeholder="e.g., UX Designer – July Sprint"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    autoComplete="off"
                    required
                />
            </div>
        </div>

        {/* ── Screening ─────────────────────────────────────────────────────────── */}
        <div className="dark:glass-effect bg-bgPrimary w-full px-6 py-4 rounded-xl border border-gray-700/50 dark:shadow-lg shadow-sm flex items-center justify-between gap-6">
            {/* Capped so the copy wraps well short of the toggle instead of running the
                full width of the card. */}
            <div className="min-w-0 max-w-3xl">
                <h3 className="text-lg font-normal dark:text-white text-textSecondary mb-2">
                    Do you want to include screening as a part of this outreach?
                </h3>
                <p className="text-sm dark:text-gray-300 text-textSecondary leading-relaxed py-2">
                    Candidates get a short screening form alongside your emails, so their answers come
                    back with the reply and knock-out criteria are applied automatically. Turn this off
                    to send plain outreach — just your messages on a schedule.
                </p>
                {typeLocked && (
                    <p className="text-[13px] dark:text-gray-400 text-textSecondary mt-2">
                        Candidates are already enrolled in this outreach, so this can&apos;t be
                        changed now. Create a new outreach if you need the other option.
                    </p>
                )}
            </div>

            <div className="shrink-0 flex items-center gap-3">
                <span className="text-sm font-medium dark:text-white text-textPrimary w-8 text-right">
                    {includeScreening ? "Yes" : "No"}
                </span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={includeScreening}
                    aria-label="Include screening as part of this outreach"
                    disabled={typeLocked}
                    onClick={() => onTypeChange(!includeScreening)}
                    className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${
                        includeScreening ? "seq-gradient-fill" : "dark:bg-gray-600 bg-gray-300"
                    } ${typeLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                    <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            includeScreening ? "translate-x-6" : ""
                        }`}
                    />
                </button>
            </div>
        </div>
    </div>
);

export default StepCreate;
